import { isArray, isObject, crush as radashCrush } from 'radash';

const SyncEngineHelpers = {
    isUnderAnyPath: (key: string, paths: string[]) => isUnderAnyPath(key, paths),

    get: <T extends object>(target: T, path: string): any => {
        const pathArray = path.split('.');

        let returnValue: any = target;

        pathArray.forEach((key) => {
            if (Array.isArray(returnValue)) {
                const _returnValue: any[] = returnValue;

                if (key.match(/^\d+$/)) {
                    returnValue = _returnValue?.[parseInt(key)];
                } else {
                    returnValue = _returnValue.find((item) => item?.id === key);
                }
            } else {
                returnValue = returnValue?.[key];
            }
        });

        return returnValue;
    },

    set: <T extends object>(target: T, path: string, value: any): T => {
        const pathArray = path.split('.');

        let returnValue: any = target;

        pathArray.forEach((key, index, pathArray) => {
            if (index === pathArray.length - 1) {
                if (Array.isArray(returnValue)) {
                    const _returnValue: any[] = returnValue;

                    if (isDigit(key)) {
                        if (value === undefined) {
                            _returnValue.splice(parseInt(key), 1);
                        } else {
                            setValue(_returnValue, parseInt(key), value);
                        }
                    } else {
                        const index = _returnValue.findIndex((item) => item?.id === key);

                        if (index === -1 && value !== undefined) {
                            _returnValue.push({
                                id: key,
                                ...value,
                            });
                        } else if (value === undefined) {
                            _returnValue.splice(index, 1);
                        } else {
                            setValue(_returnValue, index, value);
                        }
                    }
                } else {
                    if (value === undefined) {
                        delete returnValue[key];
                    } else {
                        setValue(returnValue, key, value);
                    }
                }
            } else {
                if (Array.isArray(returnValue)) {
                    const _returnValue: any[] = returnValue;

                    if (isDigit(key)) {
                        const keyAsIndex = parseInt(key);

                        if (_returnValue[keyAsIndex] === undefined || _returnValue[keyAsIndex] === null) {
                            if (isDigit(pathArray[index + 1])) {
                                _returnValue[keyAsIndex] = [];
                            } else {
                                _returnValue[keyAsIndex] = {};
                            }
                        }

                        returnValue = _returnValue[keyAsIndex];
                    } else {
                        returnValue = _returnValue.find((item) => item?.id === key);

                        if (!returnValue) {
                            throw new Error(`Object with ID ${key} not found`);
                        }
                    }
                } else {
                    if (returnValue[key] === undefined || returnValue[key] === null) {
                        if (isDigit(pathArray[index + 1])) {
                            returnValue[key] = [];
                        } else {
                            returnValue[key] = {};
                        }
                    }

                    returnValue = returnValue[key];
                }
            }
        });

        return target;
    },

    unset: <T extends object>(target: T, path: string): T => {
        const pathArray = path.split('.');

        let returnValue: any = target;

        pathArray.forEach((key, index) => {
            if (index === pathArray.length - 1) {
                if (Array.isArray(returnValue)) {
                    const _returnValue: any[] = returnValue;

                    const index = key.match(/^\d+$/)
                        ? parseInt(key)
                        : _returnValue.findIndex((item) => item?.id === key);

                    if (index !== -1) {
                        const path = pathArray.slice(0, -1).join('.');
                        SyncEngineHelpers.set(
                            target,
                            path,
                            _returnValue.filter((_, i) => i !== index),
                        );
                    }
                } else {
                    delete returnValue[key];
                }
            } else {
                if (Array.isArray(returnValue)) {
                    const _returnValue: any[] = returnValue;

                    if (key.match(/^\d+$/)) {
                        returnValue = _returnValue[parseInt(key)];
                    } else {
                        returnValue = _returnValue.find((item) => item?.id === key);
                    }
                } else {
                    if (returnValue[key] === undefined) {
                        return target;
                    }

                    returnValue = returnValue[key];
                }
            }
        });

        return target;
    },

    crush: (source: object, keysToIgnore: string[] = []) => {
        const crushed: Record<string, any> = radashCrush(source);

        // Remove keys to ignore
        for (const key of Object.keys(crushed)) {
            if (isUnderAnyPath(key, keysToIgnore)) {
                delete crushed[key];
            }
        }

        // Consolidate keys ending with a number into a single array
        const keys = Object.keys(crushed);
        for (const key of keys) {
            if (key.match(/\.\d+$/)) {
                const arrayKey = key.replace(/\.\d+$/, '');

                if (arrayKey in crushed) {
                    crushed[arrayKey].push(crushed[key]);
                } else {
                    crushed[arrayKey] = [crushed[key]];
                }

                delete crushed[key];
            }
        }

        // Search for empty array in nested object and add key to crushed object
        const addPathsToEmptyArray = (obj: any, path: string = '') => {
            if (isUnderAnyPath(path, keysToIgnore)) {
                return;
            }

            if (Array.isArray(obj) && obj.length === 0) {
                crushed[path] = [];
            } else if (typeof obj === 'object' && obj) {
                for (const key of Object.keys(obj)) {
                    addPathsToEmptyArray(obj[key], path ? `${path}.${key}` : key);
                }
            }
        };

        addPathsToEmptyArray(source);

        return crushed;
    },
};

const isDigit = (string: string) => string.match(/^\d+$/);

const isUnderAnyPath = (key: string, paths: string[]) =>
    paths.some((path) => key === path || key.startsWith(`${path}.`));

const setValue = (target: any, key: any, value: any) => {
    if (isObject(value)) {
        if (!isObject(target[key])) {
            const complete = {};

            Object.entries(value).forEach(([subKey, subValue]) => {
                setValue(complete, subKey, subValue);
            });

            target[key] = complete;
        } else {
            Object.keys(target[key]).forEach((subKey) => {
                if (!(subKey in value)) {
                    delete target[key][subKey];
                }
            });

            Object.entries(value).forEach(([subKey, subValue]) => {
                setValue(target[key], subKey, subValue);
            });
        }
    } else if (isArray(value)) {
        if (!isArray(target[key]) || value.length !== target[key]?.length) {
            target[key] = [];
        }

        Object.entries(value).forEach(([subKey, subValue]) => {
            setValue(target[key], subKey, subValue);
        });
    } else {
        try {
            target[key] = value;
        } catch (error) {
            throw new Error(
                `Cannot set value ${JSON.stringify(value)} to key ${key} in target ${JSON.stringify(target)}: ${error}`,
            );
        }
    }

    return target;
};

export { SyncEngineHelpers };
